import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios'

import { API_BASE_URL, DEFAULT_TIMEOUT_MS, EXPIRY_SKEW_MS, isAuthFreePath } from './config'
import { SessionExpiredError, toApiError } from './errors'
import { refreshTokens } from './refreshClient'
import { attachApiResponseLogger } from './responseLogger'
import { triggerSessionExpired } from './sessionExpiry'
import { tokenStore } from './tokenStore'

/** 401 재시도가 무한 루프가 되지 않도록 요청 1건당 1회로 제한한다. */
type RetryableConfig = InternalAxiosRequestConfig & { __isRetry?: boolean }

/**
 * **모든 API 호출의 단일 통로**(설계 불변식 2).
 * 예외는 인터셉터 재귀를 끊는 `refreshClient` 하나뿐이다.
 */
export const client = axios.create({
  baseURL: API_BASE_URL,
  timeout: DEFAULT_TIMEOUT_MS,
  headers: { 'Content-Type': 'application/json' },
})

attachApiResponseLogger(client)

/** 갱신을 시도하고, 세션이 끊겼으면 단일 만료 경로를 태운다. */
async function ensureFreshToken(): Promise<void> {
  try {
    await refreshTokens()
  } catch (error) {
    if (error instanceof SessionExpiredError) {
      triggerSessionExpired()
    }
    throw error
  }
}

client.interceptors.request.use(async (config) => {
  if (isAuthFreePath(config.url)) return config

  // 만료 60초 전이면 선제 갱신 — 요청이 401 로 한 번 죽는 왕복을 줄인다.
  if (tokenStore.refreshToken && tokenStore.accessTokenExpiresAt - Date.now() <= EXPIRY_SKEW_MS) {
    await ensureFreshToken()
  }

  const accessToken = tokenStore.accessToken
  if (accessToken) {
    // 🚨 여기 들어가는 값은 **항상 access 토큰**이다. refresh 토큰을 Bearer 로 보내지 않는다(T4).
    config.headers.Authorization = `Bearer ${accessToken}`
  }
  return config
})

client.interceptors.response.use(
  (response) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError)) return Promise.reject(toApiError(error))

    const config = error.config as RetryableConfig | undefined
    const status = error.response?.status

    const canRetry =
      status === 401 && config !== undefined && !config.__isRetry && !isAuthFreePath(config.url)

    if (!canRetry) {
      // 🚨 재시도 뒤에도 나온 401 은 **유효 세션의 인가 실패**다 — 세션을 지우지 않는다(불변식 5).
      return Promise.reject(toApiError(error))
    }

    try {
      await ensureFreshToken()
    } catch (refreshError) {
      return Promise.reject(
        refreshError instanceof SessionExpiredError ? refreshError : toApiError(refreshError)
      )
    }

    config.__isRetry = true
    const accessToken = tokenStore.accessToken
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }

    try {
      return await client.request(config)
    } catch (retryError) {
      return Promise.reject(toApiError(retryError))
    }
  }
)
